import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Star, MapPin, Clock, ArrowLeft, ChevronDown, ChevronUp, Heart, Globe, Facebook, Linkedin, Instagram, Phone, Search, ArrowDownUp, ShieldCheck, Check, Flame } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/i18n/I18nProvider";
import { formatCurrency } from "@/lib/currency";
import { Seo } from "@/components/seo/Seo";
import TrustBadges from "@/components/trust/TrustBadges";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { isBulkOrderEnabled } from "@/lib/featureFlags";
import BulkOrderInfo from "@/components/b2b/BulkOrderInfo";

// Remove in-file mock companies; always load from Supabase

const CompanyDetail = () => {
  const params = useParams() as { id?: string; slug?: string };
  const id = params.id;
  const slug = params.slug;
  const navigate = useNavigate();
  const { t } = useI18n();
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [company, setCompany] = useState<any | null>(null);
  const [services, setServices] = useState<any[]>([]);
  const [favId, setFavId] = useState<string | null>(null);
  const [serviceQuery, setServiceQuery] = useState<string>("");
  const [sortMode, setSortMode] = useState<"relevance" | "price_asc" | "price_desc" | "alpha">("relevance");
  const [leadTimeLabel, setLeadTimeLabel] = useState<string | null>(null);
  const [hotService, setHotService] = useState<{ service_id: string; bookings_count: number } | null>(null);
  const [featuredCountdown, setFeaturedCountdown] = useState<string>("");

  useEffect(() => {
    const load = async () => {
      // Avoid deep TS inference from generated Database types for quick UI fetches
      const sb: any = supabase;
      let dbCompany: any | null = null;
      const isUuid = (value: string | undefined) => !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
      // Prefer slug when present, otherwise id
      if (slug) {
        const { data } = await sb
          .from("companies")
          .select("id, company_name, city, logo_url, cover_image_url, rating, total_reviews, slug, description, created_at, website_url, facebook_url, linkedin_url, instagram_url, whatsapp_url, is_verified")
          .eq("slug", slug)
          .limit(1);
        dbCompany = data?.[0] || null;
      } else if (id) {
        if (!isUuid(id)) {
          const { data } = await sb
            .from("companies")
            .select("id, company_name, city, logo_url, cover_image_url, rating, total_reviews, slug, description, created_at, website_url, facebook_url, linkedin_url, instagram_url, whatsapp_url, is_verified")
            .eq("slug", id)
            .limit(1);
          dbCompany = data?.[0] || null;
        } else {
          const { data } = await sb
            .from("companies")
            .select("id, company_name, city, logo_url, cover_image_url, rating, total_reviews, slug, description, created_at, website_url, facebook_url, linkedin_url, instagram_url, whatsapp_url, is_verified")
            .eq("id", id)
            .limit(1);
          dbCompany = data?.[0] || null;
        }
      }

      if (dbCompany) {
        // Normalize DB fields to the UI shape
        const normalized: any = {
          ...dbCompany,
          name: (dbCompany as any).company_name || (dbCompany as any).name,
          image: (dbCompany as any).logo_url || (dbCompany as any).image || "/assets/placeholders/logo-placeholder.svg",
          coverPhoto: (dbCompany as any).cover_image_url || (dbCompany as any).coverPhoto || "/assets/placeholders/cover-placeholder.svg",
          rating: (dbCompany as any).rating ?? (dbCompany as any).avg_rating ?? (dbCompany as any).rating ?? 0,
          reviewCount: (dbCompany as any).total_reviews ?? (dbCompany as any).reviewCount ?? 0,
          location: (dbCompany as any).city || (dbCompany as any).location || "",
          badges: Array.isArray((dbCompany as any).badges) ? (dbCompany as any).badges : [],
          availability: (dbCompany as any).availability || "",
          reviews: Array.isArray((dbCompany as any).reviews) ? (dbCompany as any).reviews : [],
          created_at: (dbCompany as any).created_at,
          website_url: (dbCompany as any).website_url,
          facebook_url: (dbCompany as any).facebook_url,
          linkedin_url: (dbCompany as any).linkedin_url,
          instagram_url: (dbCompany as any).instagram_url,
          whatsapp_url: (dbCompany as any).whatsapp_url,
        };
        setCompany(normalized);

        const { data: svcs } = await sb
          .from("services")
          .select("id, name, description, base_price, pricing_type, is_active, included_items, benefit_items, allowed_service_types, is_public, is_deleted")
          .eq("company_id", (dbCompany as any).id)
          .eq("is_active", true)
          .eq("is_public", true)
          .is("is_deleted", false);
        setServices(svcs || []);

        // Load recent reviews from DB (public read)
        try {
          const { data: rv } = await sb
            .from('reviews')
            .select('rating, comment, created_at')
            .eq('company_id', (dbCompany as any).id)
            .order('created_at', { ascending: false })
            .limit(5);
          const mapped = (rv || []).map((r: any) => ({
            name: 'Cliente',
            rating: Number(r.rating || 0),
            comment: r.comment || '',
          })).filter((r: any) => r.rating > 0);
          setCompany((prev: any) => prev ? ({ ...prev, reviews: mapped }) : prev);
        } catch {}

        // Load favourite for logged-in user
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            const { data: fav } = await (supabase as any)
              .from('favourites')
              .select('id')
              .eq('user_id', session.user.id)
              .eq('company_id', (dbCompany as any).id)
              .maybeSingle();
            setFavId(fav?.id || null);
          }
        } catch {}
      }
    };
    load();
  }, [id, slug]);

  // If user lands on the UUID route (/company/:id), redirect to the canonical slug route (/c/:slug)
  // so we consolidate SEO signals and avoid duplicate URLs for the same company.
  useEffect(() => {
    try {
      if (slug) return; // already on /c/:slug
      if (!id) return;
      const isUuid = (value: string | undefined) =>
        !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
      if (!isUuid(id)) return; // if /company/<slug> style, don't redirect
      const companySlug = (company as any)?.slug as string | undefined;
      if (companySlug && typeof companySlug === "string" && companySlug.trim()) {
        navigate(`/c/${companySlug}`, { replace: true });
      }
    } catch {}
  }, [id, slug, company, navigate]);
  
  // Helpers and derived data must be declared before any early return,
  // otherwise React hooks will be called conditionally and crash in production.
  const hash32 = (input: string): number => {
    let h = 0;
    for (let i = 0; i < input.length; i++) {
      h = (h << 5) - h + input.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h);
  };

  const todayKey = () => {
    // Local date key (stable for the user during the day)
    try { return new Date().toLocaleDateString("en-CA"); } catch { return new Date().toISOString().slice(0, 10); }
  };

  const filteredServices = useMemo(() => {
    const all = (services && services.length > 0
      ? services
      : (Array.isArray((company as any)?.services) ? (company as any).services : [])) as any[];
    const q = serviceQuery.trim().toLowerCase();
    const base = q
      ? all.filter((s) =>
          String(s?.name || "").toLowerCase().includes(q) ||
          String(s?.description || "").toLowerCase().includes(q)
        )
      : all;

    const byName = (a: any, b: any) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "it", { sensitivity: "base" });
    const priceNum = (s: any) => {
      const v = Number(s?.base_price);
      return Number.isFinite(v) ? v : Number.POSITIVE_INFINITY;
    };

    if (sortMode === "alpha") return [...base].sort(byName);
    if (sortMode === "price_asc") return [...base].sort((a, b) => priceNum(a) - priceNum(b));
    if (sortMode === "price_desc") return [...base].sort((a, b) => priceNum(b) - priceNum(a));
    return base;
  }, [services, company, serviceQuery, sortMode]);

  const serviceCount = filteredServices.length;
  const verified = !!(company as any)?.is_verified;

  const allServices = useMemo(() => {
    return (services && services.length > 0
      ? services
      : (Array.isArray((company as any)?.services) ? (company as any).services : [])) as any[];
  }, [services, company]);

  const featuredService = useMemo(() => {
    if (!allServices || allServices.length === 0) return null;
    // Prefer "hot service" (most booked recently) when available, otherwise fall back to service-of-the-day rotation.
    if (hotService?.service_id) {
      const match = allServices.find((s: any) => String(s?.id) === String(hotService.service_id));
      if (match) return match;
    }
    const idx = hash32(`${(company as any)?.id || "c"}::${todayKey()}::svc`) % allServices.length;
    return allServices[idx] || allServices[0] || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allServices, (company as any)?.id, hotService?.service_id]);

  const featuredReview = useMemo(() => {
    const reviews = Array.isArray((company as any)?.reviews) ? (company as any).reviews : [];
    if (!reviews.length) return null;
    const idx = hash32(`${(company as any)?.id || "c"}::${todayKey()}::rev`) % reviews.length;
    const r = reviews[idx] || reviews[0];
    const text = String(r?.comment || "").trim();
    const trimmed = text.length > 120 ? `${text.slice(0, 117)}…` : text;
    return { rating: Number(r?.rating || 0), text: trimmed };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(company as any)?.id, (company as any)?.reviews]);

  // Load "hot services" (aggregated counts) for honest urgency messaging.
  useEffect(() => {
    (async () => {
      try {
        if (!(company as any)?.id) { setHotService(null); return; }
        const { data, error } = await (supabase as any).rpc('get_company_hot_services', {
          p_company_id: (company as any).id,
          p_days: 7,
          p_limit: 1,
        });
        if (error) { setHotService(null); return; }
        const row = Array.isArray(data) ? data[0] : null;
        if (row?.service_id && Number(row.bookings_count || 0) > 0) {
          setHotService({ service_id: String(row.service_id), bookings_count: Number(row.bookings_count || 0) });
        } else {
          setHotService(null);
        }
      } catch {
        setHotService(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(company as any)?.id]);

  // Countdown to end-of-day (local) to create urgency without faking a discount.
  useEffect(() => {
    const tick = () => {
      try {
        const now = new Date();
        const end = new Date(now);
        end.setHours(23, 59, 59, 999);
        const ms = end.getTime() - now.getTime();
        const total = Math.max(0, Math.floor(ms / 1000));
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        const pad = (n: number) => String(n).padStart(2, "0");
        setFeaturedCountdown(`${pad(h)}:${pad(m)}:${pad(s)}`);
      } catch {
        setFeaturedCountdown("");
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);

  // Compute an honest "prime disponibilità" label from vendor availability + existing bookings.
  // If vendor doesn't maintain availability, we fall back to a safe message.
  useEffect(() => {
    (async () => {
      try {
        if (!(company as any)?.id) { setLeadTimeLabel(null); return; }
        const sb: any = supabase;
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(end.getDate() + 13);
        const startKey = start.toISOString().slice(0, 10);
        const endKey = end.toISOString().slice(0, 10);

        const { data: av } = await sb
          .from("vendor_availability")
          .select("date, capacity, blocked")
          .eq("company_id", (company as any).id)
          .gte("date", startKey)
          .lte("date", endKey);

        const { data: bs } = await sb
          .from("bookings")
          .select("scheduled_date, status")
          .eq("company_id", (company as any).id)
          .gte("scheduled_date", startKey)
          .lte("scheduled_date", endKey);

        const map: Record<string, { cap: number; blocked: boolean; count: number }> = {};
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const k = d.toISOString().slice(0, 10);
          map[k] = { cap: 5, blocked: false, count: 0 };
        }
        (av || []).forEach((r: any) => {
          const k = String(r.date);
          if (!map[k]) map[k] = { cap: 5, blocked: false, count: 0 };
          map[k].cap = (typeof r.capacity === "number" ? Number(r.capacity) : 5);
          map[k].blocked = !!r.blocked;
        });
        (bs || []).forEach((b: any) => {
          if (String(b.status) !== "cancelled") {
            const k = String(b.scheduled_date);
            if (!map[k]) map[k] = { cap: 5, blocked: false, count: 0 };
            map[k].count += 1;
          }
        });

        let first: string | null = null;
        for (const k of Object.keys(map).sort()) {
          const row = map[k];
          const cap = Number.isFinite(row.cap) ? row.cap : 0;
          if (!row.blocked && cap > 0 && row.count < cap) {
            first = k;
            break;
          }
        }

        if (!first) {
          setLeadTimeLabel(t("company.lead_time_fallback", "Scegli data e ora al prossimo step"));
          return;
        }

        const firstDate = new Date(first + "T00:00:00");
        const diffDays = Math.round((firstDate.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
        if (diffDays <= 0) setLeadTimeLabel(t("company.lead_time_today", "Prime disponibilità: oggi"));
        else if (diffDays === 1) setLeadTimeLabel(t("company.lead_time_24h", "Prime disponibilità: entro 24h"));
        else if (diffDays <= 2) setLeadTimeLabel(t("company.lead_time_48h", "Prime disponibilità: entro 48h"));
        else {
          const pretty = firstDate.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
          setLeadTimeLabel(t("company.lead_time_date", `Prime disponibilità: ${pretty}`));
        }
      } catch {
        setLeadTimeLabel(t("company.lead_time_fallback", "Scegli data e ora al prossimo step"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(company as any)?.id]);

  if (!company) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Seo title="Azienda non trovata | Halomora" canonicalPath="/" />
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">{t('company.not_found','Azienda non trovata')}</h1>
          <Button onClick={() => navigate("/")} variant="outline">
            {t('company.return_home','Torna alla Home')}
          </Button>
        </div>
      </div>
    );
  }

  const canonicalPath = (company as any)?.slug ? `/c/${(company as any).slug}` : `/company/${company.id}`;
  const seoTitle = `${(company as any).company_name || company.name}${company.location ? ` • ${company.location}` : ''} | Halomora`;
  const seoDescription =
    (company.description && String(company.description).trim())
      ? String(company.description).trim()
      : `Prenota servizi domestici e professionali con ${(company as any).company_name || company.name}. Recensioni reali, prezzi chiari e disponibilità rapida.`;

  const handleServiceSelect = (serviceId: string) => {
    navigate(`/booking/${company.id}/${serviceId}`);
  };

  const scrollToServices = () => {
    try {
      const el = document.getElementById("company-services");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Focus search for faster scanning when user arrives
      window.setTimeout(() => {
        const input = document.getElementById("company-services-search") as HTMLInputElement | null;
        input?.focus();
      }, 250);
    } catch {}
  };

  const toggleFavourite = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) { navigate('/auth'); return; }
      if (favId) {
        await (supabase as any).from('favourites').delete().eq('id', favId);
        setFavId(null);
      } else {
        const ins = await (supabase as any).from('favourites').insert({ user_id: session.user.id, company_id: company.id }).select('id').maybeSingle();
        if (!ins.error) setFavId(ins.data?.id || null);
      }
    } catch {}
  };

  const toggleFAQ = (serviceId: string) => {
    setExpandedFAQ(expandedFAQ === serviceId ? null : serviceId);
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo title={seoTitle} description={seoDescription} canonicalPath={canonicalPath} />
      {/* Header */}
      <div className="app-hero py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-col gap-4">
            <div>
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                className="text-primary-foreground hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('company.back_to_search','Torna alla ricerca')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 pb-24">
        {/* Cover Photo */}
        <div className="relative h-64 rounded-xl overflow-hidden mb-8 shadow-strong">
          <img 
            src={company.coverPhoto} 
            alt={`${company.name} work`}
            className="w-full h-full object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = "/assets/placeholders/cover-placeholder.svg"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

          {/* Featured service overlay (right side of cover) */}
          {featuredService && (
            <div className="absolute top-4 right-4 left-4 md:left-auto md:w-[420px]">
              <div className="rounded-2xl border border-white/15 bg-background/90 backdrop-blur shadow-strong p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="gap-1">
                      <Flame className="h-3.5 w-3.5" />
                      {hotService?.bookings_count && hotService.bookings_count >= 2
                        ? t("company.hot_badge", "HOT")
                        : t("company.featured_badge", "In evidenza")}
                    </Badge>
                    {hotService?.bookings_count && hotService.bookings_count >= 2 && (
                      <span className="text-xs text-muted-foreground">
                        {t("company.hot_subtitle", `${hotService.bookings_count} prenotazioni (ultimi 7 giorni)`)}
                      </span>
                    )}
                  </div>
                  {featuredCountdown ? (
                    <span className="text-xs text-muted-foreground">
                      {t("company.featured_timer", `Scade tra ${featuredCountdown}`)}
                    </span>
                  ) : null}
                </div>

                <div className="mt-2 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-foreground leading-snug whitespace-normal break-words">
                      {String(featuredService.name || "")}
                    </div>
                    {featuredService.base_price ? (
                      <div className="text-sm text-muted-foreground mt-1">
                        {t("company.from_price", "da")}{" "}
                        <span className="font-semibold text-foreground">
                          {formatCurrency(Number(featuredService.base_price))}
                        </span>
                      </div>
                    ) : null}
                  </div>
                  <Button
                    variant="hero"
                    onClick={() => handleServiceSelect(String(featuredService.id))}
                    className="shrink-0"
                  >
                    {t("company.book_now", "PRENOTA ORA")}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Card className="bg-gradient-card border-0 shadow-medium">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <img
                    src={company.image}
                    alt={company.name}
                    className="w-20 h-20 rounded-xl object-cover shadow-medium"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).src = "/assets/placeholders/logo-placeholder.svg"; }}
                  />
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-foreground">{(company as any).company_name || company.name}</h1>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {verified && (
                        <Badge variant="secondary" className="gap-1">
                          <ShieldCheck className="h-3.5 w-3.5" />
                          {t('company.verified','Impresa verificata')}
                        </Badge>
                      )}
                      <Badge variant="secondary" className="gap-1">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        {t('company.secure_payment','Pagamento sicuro')}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Star className="h-5 w-5 fill-current text-yellow-400" />
                      <span className="text-lg font-bold text-foreground">{Number(company.rating || 0).toFixed(1)}</span>
                      <span className="text-muted-foreground">({((company as any).reviewCount ?? (company as any).total_reviews ?? 0)} {t('company.reviews','recensioni')})</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">{company.location}</span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-3 mb-4">
                  {/* Trust bullets (compact) */}
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-success" />
                      <span>{t("company.trust_secure_payment", "Pagamento sicuro")}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-success" />
                      <span>{t("company.trust_real_reviews", "Recensioni reali")}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-success" />
                      <span>{t("company.trust_fast_booking", "Prenotazione online in pochi minuti")}</span>
                    </li>
                  </ul>

                  {/* Rotating review snippet (no fake fallback) */}
                  <div className="text-xs text-muted-foreground">
                    {featuredReview ? (
                      <div className="flex items-start gap-2">
                        <Star className="h-4 w-4 text-yellow-400 fill-current mt-0.5" />
                        <div className="min-w-0">
                          <span className="text-foreground font-medium">{featuredReview.rating.toFixed(0)}/5</span>{" "}
                          <span className="italic">“{featuredReview.text}”</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Star className="h-4 w-4 text-muted-foreground/60" />
                        <span>{t("company.no_reviews_yet", "Nessuna recensione ancora su Halomora")}</span>
                      </div>
                    )}
                  </div>

                  {/* Availability lead time (safe) */}
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span>{leadTimeLabel || t("company.lead_time_loading", "Verifica disponibilità…")}</span>
                  </div>

                  {/* Secondary action: show all services */}
                  <Button variant="outline" onClick={scrollToServices} className="w-full">
                    {t("company.view_all_services", "Vedi tutti i servizi")}
                  </Button>
                </div>

                <p className="text-muted-foreground mb-4">{company.description}</p>
                {/* Social Links */}
                <div className="flex items-center gap-3 mb-4">
                  {company.website_url && <a href={company.website_url} target="_blank" rel="noreferrer" className="text-foreground hover:text-primary"><Globe className="h-5 w-5" /></a>}
                  {company.facebook_url && <a href={company.facebook_url} target="_blank" rel="noreferrer" className="text-foreground hover:text-primary"><Facebook className="h-5 w-5" /></a>}
                  {company.linkedin_url && <a href={company.linkedin_url} target="_blank" rel="noreferrer" className="text-foreground hover:text-primary"><Linkedin className="h-5 w-5" /></a>}
                  {company.instagram_url && <a href={company.instagram_url} target="_blank" rel="noreferrer" className="text-foreground hover:text-primary"><Instagram className="h-5 w-5" /></a>}
                  {company.whatsapp_url && <a href={company.whatsapp_url} target="_blank" rel="noreferrer" className="text-foreground hover:text-primary"><Phone className="h-5 w-5" /></a>}
                </div>
                
                {/* Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {Array.isArray((company as any).badges) && (company as any).badges.map((badge: any, index: number) => (
                    <Badge key={index} variant="secondary">
                      {badge}
                    </Badge>
                  ))}
                </div>

                {/* Footer row */}
                <div className="flex items-center gap-2 p-3 bg-success/10 rounded-lg">
                  <Clock className="h-4 w-4 text-success" />
                  <span className="text-success font-medium">{t('company.member_since','Membro dal')} {company.created_at ? new Date(company.created_at).getFullYear() : '—'}</span>
                  <div className="ml-auto">
                    <Button variant={favId ? 'default' : 'outline'} size="sm" onClick={toggleFavourite}>
                      <Heart className={`h-4 w-4 mr-2 ${favId ? 'fill-white' : ''}`} />
                      {favId ? t('company.favourited','Aggiunto ai preferiti') : t('company.add_favourite','Aggiungi ai preferiti')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Reviews */}
            {Array.isArray((company as any).reviews) && (company as any).reviews.length > 0 && (
              <Card className="mt-6 bg-gradient-card border-0 shadow-medium">
                <CardHeader>
                  <CardTitle className="text-foreground">{t('company.recent_reviews','Recensioni recenti')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(company as any).reviews.map((review: any, index: number) => (
                    <div key={index} className="border-b border-border/50 last:border-0 pb-4 last:pb-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-foreground">{review.name}</span>
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Star
                              key={i}
                              className={`h-3 w-3 ${i <= Number(review.rating || 0) ? "fill-current text-yellow-400" : "text-muted-foreground/30"}`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">{review.comment}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Services */}
          <div className="lg:col-span-2" id="company-services">
            {/* Trust Badges - quando sceglie l'azienda */}
            <Card className="mb-6 bg-gradient-to-br from-primary/5 to-accent-green-500/5 border-primary/20">
              <CardContent className="p-4">
                <div className="text-center mb-3">
                  <h3 className="text-sm font-semibold text-gray-900">Prenotazione sicura e protetta</h3>
                </div>
                <TrustBadges variant="compact" className="justify-center" />
              </CardContent>
            </Card>

            <div className="flex flex-col gap-4 mb-6">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <h2 className="text-2xl font-bold text-foreground">{t('company.services_pricing','Servizi e prezzi')}</h2>
                <div className="text-sm text-muted-foreground">
                  {serviceCount} {t('company.services_count','servizi')}
                </div>
              </div>
              <p className="text-sm text-muted-foreground border-l-4 border-primary/25 pl-3 py-1">
                {t(
                  'company.booking_modes_explainer',
                  'Modalità e prezzo dipendono da data e orario scelti in checkout (conferma immediata, richiesta al vendor o urgente). Per il giorno stesso del servizio, le prenotazioni sono accettate solo prima delle 08:00 (ora Italia).'
                )}
              </p>
              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    id="company-services-search"
                    value={serviceQuery}
                    onChange={(e) => setServiceQuery(e.target.value)}
                    placeholder={t('company.search_services','Cerca un servizio…')}
                    className="pl-9"
                  />
                </div>
                <div className="w-full md:w-[240px]">
                  <Select value={sortMode} onValueChange={(v) => setSortMode((v as any) || "relevance")}>
                    <SelectTrigger>
                      <div className="flex items-center gap-2">
                        <ArrowDownUp className="h-4 w-4 text-muted-foreground" />
                        <SelectValue placeholder={t('company.sort','Ordina')} />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="relevance">{t('company.sort_relevance','Consigliati')}</SelectItem>
                      <SelectItem value="price_asc">{t('company.sort_price_asc','Prezzo: crescente')}</SelectItem>
                      <SelectItem value="price_desc">{t('company.sort_price_desc','Prezzo: decrescente')}</SelectItem>
                      <SelectItem value="alpha">{t('company.sort_alpha','Alfabetico')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              {filteredServices.map((service: any) => (
                <Card key={service.id} className="bg-gradient-card border-0 shadow-medium">
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 min-w-0">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-semibold text-foreground">{service.name}</h3>
                        <p className="text-muted-foreground mt-1">{service.description}</p>
                        {Array.isArray(service.allowed_service_types) && service.allowed_service_types.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {service.allowed_service_types.slice(0, 4).map((k: string) => (
                              <Badge key={k} variant="secondary" className="text-xs">
                                {String(k).replace(/_/g, " ")}
                              </Badge>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-4 mt-3">
                          <span className="text-2xl font-bold text-primary">{service.base_price ? formatCurrency(Number(service.base_price)) : (service.price || "")}</span>
                          <span className="text-sm text-muted-foreground">{service.pricing_type || service.priceCalculator}</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 w-full sm:w-auto sm:items-end">
                        <Button 
                          onClick={() => handleServiceSelect(service.id)}
                          variant="hero"
                          className="ml-0 sm:ml-4 w-full sm:w-auto text-base font-semibold shadow-lg hover:shadow-xl transition-all"
                          size="lg"
                        >
                          {t('company.select_service','Seleziona servizio')} →
                        </Button>
                        {isBulkOrderEnabled() && (
                          <div className="ml-0 sm:ml-4 w-full sm:w-auto flex items-center gap-1.5">
                            <Button
                              onClick={() => navigate(`/booking/bulk/${company.id}/${service.id}`)}
                              variant="outline"
                              size="lg"
                              className="w-full sm:w-auto border-primary/30 text-primary hover:bg-primary/5 hover:border-primary/50"
                            >
                              <span className="flex items-center gap-2">
                                BULK ORDER
                                <Badge variant="secondary" className="h-5 px-2 text-[11px] leading-5">
                                  beta
                                </Badge>
                              </span>
                            </Button>
                            <BulkOrderInfo
                              className="shrink-0"
                              tooltipText="Per apartment manager: carica fino a 30 ordini in un colpo (checkout/check-in)."
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <Separator className="mb-4" />
                    <Button
                      variant="ghost"
                      onClick={() => toggleFAQ(service.id)}
                      className="w-full justify-between p-0 h-auto font-normal text-left"
                    >
                      <span className="text-foreground">{t('company.faq_details','FAQ e dettagli')}</span>
                      {expandedFAQ === service.id ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                    
                    {expandedFAQ === service.id && (
                      <div className="mt-4 space-y-4 animate-fade-in">
                        <div>
                          <h4 className="font-medium text-foreground mb-2">{t('company.whats_included','Cosa è incluso?')}</h4>
                          <ul className="list-disc pl-5 space-y-1">
                            {Array.isArray((service as any).included_items) && (service as any).included_items.length > 0 ? (
                              (service as any).included_items.map((itm: string, idx: number) => (
                                <li key={idx} className="text-sm text-muted-foreground">{itm}</li>
                              ))
                            ) : (
                              <li className="text-sm text-muted-foreground">{t('company.no_included','Il venditore non ha specificato i dettagli per questo servizio.')}</li>
                            )}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-medium text-foreground mb-2">{t('company.why_choose','Perché scegliere questa azienda')}</h4>
                          <ul className="list-disc pl-5 space-y-1">
                            {Array.isArray((service as any).benefit_items) && (service as any).benefit_items.length > 0 ? (
                              (service as any).benefit_items.map((b: string, idx: number) => (
                                <li key={idx} className="text-sm text-muted-foreground">{b}</li>
                              ))
                            ) : (
                              <li className="text-sm text-muted-foreground">{t('company.no_benefits','Vantaggi non forniti dal venditore.')}</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {filteredServices.length === 0 && (
                <Card className="bg-gradient-card border-0 shadow-medium">
                  <CardContent className="py-8 text-center text-muted-foreground">
                    {t('company.no_services_found','Nessun servizio trovato. Prova a cambiare ricerca o ordinamento.')}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky mobile bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden">
        <div className="mx-auto max-w-screen-lg px-4 pb-3">
          <div className="rounded-xl border border-border/60 bg-background/95 backdrop-blur shadow-strong p-3 flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm">
                <Star className="h-4 w-4 text-yellow-400 fill-current" />
                <span className="font-semibold text-foreground">{Number(company.rating || 0).toFixed(1)}</span>
                <span className="text-muted-foreground">
                  ({((company as any).reviewCount ?? (company as any).total_reviews ?? 0)})
                </span>
              </div>
              {featuredService?.base_price ? (
                <div className="text-xs text-muted-foreground truncate">
                  {t("company.from_price", "da")}{" "}
                  <span className="font-semibold text-foreground">
                    {formatCurrency(Number(featuredService.base_price))}
                  </span>{" "}
                  · {String(featuredService.name || "")}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground truncate">
                  {featuredService?.name ? String(featuredService.name) : t("company.services_pricing", "Servizi e prezzi")}
                </div>
              )}
            </div>
            <Button
              variant="hero"
              className="shrink-0"
              onClick={() => {
                if (featuredService?.id) handleServiceSelect(String(featuredService.id));
                else scrollToServices();
              }}
            >
              {t("company.book_now", "Prenota")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetail;